import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { useToast } from "../../components/ui/use-toast";
import { X, Loader2, AlertTriangle, Camera, MinusCircle } from "lucide-react";
import { t, Trans } from '@lingui/macro';
import { useState, useEffect, useRef } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Workspace } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Perm, FreeWorkspaceCheckResult } from "../../services/Permission";
import { ResizeImage } from "../../services/Utils/ImageResizer";
import { UUID } from "../../models/local/Uid";

export const WorkspaceCreateModal: React.FC<{ show: boolean, setShow: (v: boolean) => void }> = ({ show, setShow }) => {
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [inProgress, setInProgress] = useState(false);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [freeWorkspaceCheck, setFreeWorkspaceCheck] = useState<FreeWorkspaceCheckResult | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>("");
  const iconInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const history = useHistory();

  useEffect(() => {
    if (show) {
      setWorkspaceId("");
      setWorkspaceName("");
      setDescription("");
      setInProgress(false);
      setIconFile(null);
      setIconPreview("");

      // 무료 워크스페이스 생성 가능 여부 확인
      checkFreeWorkspaceLimit();
    } else {
      // 모달 닫힐 때 상태 초기화
      setFreeWorkspaceCheck(null);
    }
  }, [show]);

  async function checkFreeWorkspaceLimit() {
    setIsCheckingLimit(true);
    try {
      const result = await Perm.canCreateFreeWorkspace();
      setFreeWorkspaceCheck(result);
    } catch (error) {
      console.error('Failed to check free workspace limit:', error);
      // 에러 시 생성 허용 (graceful degradation)
      setFreeWorkspaceCheck({ canCreate: true });
    } finally {
      setIsCheckingLimit(false);
    }
  }

  function isWorkspaceIdValid() {
    return workspaceId.length > 3 && workspaceId.length <= 24 && /^[a-z0-9-_]+$/.test(workspaceId);
  }

  function isWorkspaceNameValid() {
    return workspaceName.length > 3 && workspaceName.length <= 36;
  }

  function isInputsValid() {
    return isWorkspaceIdValid() && isWorkspaceNameValid();
  }

  // 생성 가능 여부 (입력 유효성 + 무료 워크스페이스 제한)
  function canCreate() {
    return isInputsValid() && (freeWorkspaceCheck?.canCreate !== false);
  }

  async function saveWorkspace() {
    if (!canCreate()) return;

    setInProgress(true);
    try {
      const workspace = new Workspace(workspaceId, workspaceName, My.user);
      workspace.id = workspaceId;
      workspace.name = workspaceName;
      workspace.description = description;

      // 아이콘 이미지가 있으면 먼저 업로드
      if (iconFile) {
        const iconUrl = await new Promise<string | null>((resolve) => {
          Fire.uploadFile(iconFile, `/workspaces/${workspaceId}/icon/${UUID.get()}.jpg`, (url) => {
            resolve(url);
          }, null);
        });
        workspace.iconUrl = iconUrl;
      }

      const res = await Fire.ws.createWorkspace({ workspace });
      if (res === 'SUCCESS') {
        toast({ title: t`Workspace was created`, duration: 2000 });
        setShow(false);
        history.replace(`/ws/${workspaceId}`);
      } else if (res === 'TAKEN') {
        toast({ title: t`Workspace ID is already taken. Try a different ID.`, duration: 3000 });
      }
    } catch (error) {
      console.error(error);
      toast({ title: (error as Error).message, duration: 3000 });
    } finally {
      setInProgress(false);
    }
  }


  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col p-6 gap-6 overflow-hidden [&>button]:hidden">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 p-0">
          <Button variant="ghost" size="icon" onClick={() => setShow(false)} className="-ml-2">
            <X className="w-5 h-5" />
          </Button>
          <DialogTitle className="text-xl font-semibold"><Trans>Create a workspace</Trans></DialogTitle>
          <Button
            onClick={saveWorkspace}
            disabled={!canCreate() || inProgress || isCheckingLimit}
            className="bg-primary hover:bg-primary/90 disabled:opacity-50"
          >
            {(inProgress || isCheckingLimit) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Save</Trans>
          </Button>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-2 overflow-y-auto">
          {/* Workspace Icon Upload */}
          <div className="flex flex-col items-center gap-3">
            <div
              className="relative w-20 h-20 rounded-2xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors group overflow-hidden"
              onClick={() => iconInputRef.current?.click()}
            >
              {iconPreview ? (
                <img src={iconPreview} alt="icon" className="w-full h-full object-cover rounded-2xl" />
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground group-hover:text-primary transition-colors">
                  <Camera className="w-6 h-6" />
                  <span className="text-meta-sm font-medium"><Trans>Icon</Trans></span>
                </div>
              )}
            </div>
            {iconPreview && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2 text-xs"
                onClick={() => { setIconFile(null); setIconPreview(""); }}
              >
                <MinusCircle className="w-3 h-3 mr-1" />
                <Trans>Remove</Trans>
              </Button>
            )}
            <input
              ref={iconInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const resized = await ResizeImage(file, 256);
                  setIconFile(resized);
                  setIconPreview(URL.createObjectURL(resized));
                }
                e.target.value = '';
              }}
            />
          </div>

          {/* 무료 워크스페이스 제한 경고 배너 */}
          {freeWorkspaceCheck && !freeWorkspaceCheck.canCreate && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-amber-700">
                  <Trans>Free workspace limit reached</Trans>
                </p>
                <p className="text-xs text-amber-600">
                  <Trans>
                    You can only have {freeWorkspaceCheck.maxFreeWorkspaces} free workspace at a time.
                    Please upgrade an existing workspace to a paid plan.
                  </Trans>
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label><Trans>Workspace ID</Trans></Label>
            <Input
              value={workspaceId}
              onChange={e => setWorkspaceId(e.target.value.toLowerCase())}
              placeholder={t`Enter a unique id`}
              maxLength={24}
              disabled={inProgress || (freeWorkspaceCheck?.canCreate === false)}
            />
            {workspaceId.length > 0 && !isWorkspaceIdValid() && (
              <p className="text-xs text-destructive">
                <Trans>ID must be 4-24 characters (lowercase, numbers, -, _)</Trans>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              <Trans>This will be used in the URL: /ws/{workspaceId}</Trans>
            </p>
          </div>

          <div className="space-y-2">
            <Label><Trans>Workspace Name</Trans></Label>
            <Input
              value={workspaceName}
              onChange={e => setWorkspaceName(e.target.value)}
              placeholder={t`Enter a name`}
              maxLength={36}
              disabled={inProgress || (freeWorkspaceCheck?.canCreate === false)}
            />
            {workspaceName.length > 0 && !isWorkspaceNameValid() && (
              <p className="text-xs text-destructive">
                <Trans>Name must be 4-36 characters</Trans>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label><Trans>Description</Trans></Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t`No description`}
              maxLength={400}
              className="resize-none h-32"
              disabled={inProgress || (freeWorkspaceCheck?.canCreate === false)}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WorkspaceCreateModal;